import {
  BlueprintQuoteResponse,
  BlueprintQuoteUpdateRequest,
  CollaborationAccessEnum,
  ContractEndActionEnum,
  DocumentParentEnum,
  ESignRecipientResponse,
  IBillingScheduleRespSchemaUI,
  IContract,
  IContractRenewalReqSchema,
  IContractWithQuotes,
  IExtendedExpirationDateReqSchema,
  ImportDocumentRequest,
  INewQuoteTypeReqSchema,
  IOpportunityReqSchema,
  IOpportunitySummary,
  IOpportunityWithQuotes,
  IQuoteBasicRespSchema,
  IQuoteBillingScheduleRespSchema,
  IQuoteConditionalTermSchema,
  IQuoteContactAddressDataSchema,
  IQuoteContacts,
  IQuoteContactsRequest,
  IQuoteDiscounts,
  IQuoteOfferingDeltaView,
  IQuoteOfferingRemoveReqSchema,
  IQuoteOfferingReqSchema,
  IQuoteOfferingRespSchema,
  IQuotePrice,
  IQuoteRequestSchema,
  IQuoteRespSchema,
  IQuoteReviewReq,
  IQuoteSigningOrderResp,
  IQuoteTemplateConfigSchema,
  IStorage,
  ISubscriptionListPricingResponseSchema,
  ISubscriptionsFormReq,
  MarkQuoteAsBlueprintRequest,
  OpportunityReqSchema,
  ProductTypeEnum,
  QuoteAdditionalFields,
  QuoteCancelationReasonEnum,
  QuoteDocumentConfig,
  QuoteDocumentConfigPositionEnum,
  QuoteEditRequest,
  QuoteItemAmendmentStatusEnum,
  QuoteOfferingGroupRemovalModeEnum,
  QuoteOfferingGroupRequest,
  QuoteOfferingGroups,
  QuoteOfferingRemovalScopeEnum,
  QuoteRevenueMetrics,
  QuoteShareAccessRequest,
  QuoteStatusEnum,
  RateBillingFrequencyEnum,
  RecordShareAccessResponse,
  ReOrderRequestTypes,
  SigningOrderEnum,
  StorageTypeEnum,
  UpdateQuoteDocument,
} from '@monetize/types/app';
import {
  ApiListResponse,
  GetListApiConfig,
  GetListApiFilter,
  Maybe,
  MimeTypeEnum,
} from '@monetize/types/common';
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import { AxiosHeaders } from 'axios';
import { addDays } from 'date-fns/addDays';
import { formatISO } from 'date-fns/formatISO';
import { isBefore } from 'date-fns/isBefore';
import { parseISO } from 'date-fns/parseISO';
import isNull from 'lodash/isNull';
import { sortByProductObjType, sortByProductType } from '../utils';
import { orderObjectsBy } from '../utils/misc';
import {
  applyQuoteOfferingGroupMembership,
  sortQuoteItems,
} from '../utils/quotes';
import api, {
  apiDelete,
  apiGet,
  apiGetAllList,
  apiPost,
  apiPut,
  apiUpload,
  MAxiosRequestConfig,
} from './axios';
import { ApiQueryItem, useGetById } from './queryUtils';
import { updateListCacheWithUpdatedItem } from './queryUtilsHelpers';
import { enqueueForQuote } from './quoteWriteChain';
import {
  asQueryUtil,
  composeGetQuery,
  getQuoteUpdateRequestFromQuote,
} from './utils';

export const cpqServiceQueryKeys = {
  base: ['cpq'] as const,
  contracts: () => [...cpqServiceQueryKeys.base, 'contracts'] as const,
  contractList: () => [...cpqServiceQueryKeys.contracts(), 'list'] as const,
  // Nested under contractList so list invalidations also refresh lineages
  accountContractsAll: () =>
    [...cpqServiceQueryKeys.contractList(), 'account-all'] as const,
  accountContractsAllByAccount: (accountId: string) =>
    [...cpqServiceQueryKeys.accountContractsAll(), accountId] as const,
  contractById: (id: string) =>
    [...cpqServiceQueryKeys.contracts(), id] as const,
  billingScheduleByContractId: (id: string) =>
    [...cpqServiceQueryKeys.contracts(), id, 'billing-schedule'] as const,
  opportunities: () => [...cpqServiceQueryKeys.base, 'opportunities'] as const,
  opportunityList: () =>
    [...cpqServiceQueryKeys.opportunities(), 'list'] as const,
  opportunityById: (id: string) =>
    [...cpqServiceQueryKeys.opportunities(), id] as const,
  quotes: () => [...cpqServiceQueryKeys.base, 'quotes'] as const,
  quotesById: (id: string) => [...cpqServiceQueryKeys.quotes(), id] as const,
  quotesBasic: () => [...cpqServiceQueryKeys.base, 'quotes-basic'] as const,
  quotesBasicList: () =>
    [...cpqServiceQueryKeys.quotesBasic(), 'list'] as const,
  quoteBillingSchedule: (quoteId: string) =>
    [...cpqServiceQueryKeys.quotesById(quoteId), 'billing-schedule'] as const,
  esignRecipients: (quoteId: string) =>
    [...cpqServiceQueryKeys.quotesById(quoteId), 'esign-recipients'] as const,
  displayConfig: (quoteId: string) =>
    [...cpqServiceQueryKeys.quotesById(quoteId), 'display-config'] as const,
  quoteStorage: (quoteId: string) =>
    [...cpqServiceQueryKeys.quotes(), quoteId, 'storages'] as const,
  quoteStorageById: (quoteId: string, storageId: string) =>
    [...cpqServiceQueryKeys.quoteStorage(quoteId), storageId] as const,
  quotePrices: (quoteId: string) =>
    [...cpqServiceQueryKeys.quotes(), quoteId, 'prices'] as const,
  quoteDiscounts: (quoteId: string) =>
    [...cpqServiceQueryKeys.quotes(), quoteId, 'discounts'] as const,
  quoteTemplateHtml: (quoteId: string) =>
    [...cpqServiceQueryKeys.quotes(), quoteId, 'html'] as const,
  quoteDeltaview: (quoteId: string) =>
    [...cpqServiceQueryKeys.quotes(), quoteId, 'deltaview'] as const,
  recordShareAccess: (quoteId: string) =>
    [
      ...cpqServiceQueryKeys.quotesById(quoteId),
      'record-share-access',
    ] as const,
  recordShareEngagements: (quoteId: string) =>
    [
      ...cpqServiceQueryKeys.quotesById(quoteId),
      'record-share-engagements',
    ] as const,
  revenueMetrics: (quoteId: string) =>
    [...cpqServiceQueryKeys.quotesById(quoteId), 'annual-breakdown'] as const,
  quoteDocumentConfigs: (quoteId: string) =>
    [...cpqServiceQueryKeys.quotesById(quoteId), 'document-configs'] as const,
  availableQuoteDocumentConfigs: (quoteId: string) =>
    [
      ...cpqServiceQueryKeys.quotesById(quoteId),
      'available-document-configs',
    ] as const,
  blueprints: () => [...cpqServiceQueryKeys.base, 'blueprints'] as const,
  blueprintList: () => [...cpqServiceQueryKeys.blueprints(), 'list'] as const,
  blueprintById: (id: string) =>
    [...cpqServiceQueryKeys.blueprints(), id] as const,

  quoteOfferingGroups: (quoteId: string) =>
    [...cpqServiceQueryKeys.quotesById(quoteId), 'offering-groups'] as const,
};

const quotesKeys: ApiQueryItem = {
  byId: {
    endpoint: (id: string) => `/api/quotes/${id}`,
    queryKey: (id: string) => cpqServiceQueryKeys.quotesById(id),
  },
  create: {
    endpoint: () => `/api/quotes`,
    invalidateKeys: [cpqServiceQueryKeys.quotesBasicList()],
    setDataKey: (id: string) => cpqServiceQueryKeys.quotesById(id),
  },
  update: {
    endpoint: (id: string) => `/api/quotes/${id}`,
    invalidateKeys: [cpqServiceQueryKeys.quotesBasicList()],
    setDataKey: (id: string) => cpqServiceQueryKeys.quotesById(id),
  },
};

/** Quote list has a reduced payload for increased performance */
const quotesBasicKeys: ApiQueryItem = {
  list: {
    endpoint: `/api/quotes`,
    queryKey: cpqServiceQueryKeys.quotesBasicList(),
  },
};

const blueprintQuotesKeys: ApiQueryItem = {
  list: {
    endpoint: `/api/blueprintQuotes`,
    queryKey: cpqServiceQueryKeys.blueprintList(),
  },
  byId: {
    endpoint: (id: string) => `/api/blueprintQuotes/${id}`,
    queryKey: (id: string) => cpqServiceQueryKeys.blueprintById(id),
  },
  update: {
    endpoint: (id: string) => `/api/blueprintQuotes/${id}`,
    invalidateKeys: [cpqServiceQueryKeys.blueprintList()],
    setDataKey: (id: string) => cpqServiceQueryKeys.blueprintById(id),
  },
};

const queryKeysOpportunities: ApiQueryItem = {
  byId: {
    endpoint: (id: string) => `/api/opportunities/${id}`,
    queryKey: (id: string) => cpqServiceQueryKeys.opportunityById(id),
  },
};

export const CPQ_SERVICE_API = asQueryUtil({
  cpqServiceQuotes: quotesKeys,
  cpqServiceBasicQuotes: quotesBasicKeys,
  cpqBlueprintQuotes: blueprintQuotesKeys,
  cpqOpportunities: queryKeysOpportunities,
});

export function useGetQuoteList<
  SelectData = ApiListResponse<IQuoteBasicRespSchema>,
>(
  {
    config,
    filters,
    accountId,
  }: {
    config: GetListApiConfig;
    filters?: GetListApiFilter;
    accountId?: string;
  },
  options: Partial<
    UseQueryOptions<ApiListResponse<IQuoteBasicRespSchema>, unknown, SelectData>
  > = {},
) {
  const params = composeGetQuery(config, filters);
  if (accountId) {
    params.accountId = accountId;
  }
  return useQuery({
    queryKey: [...cpqServiceQueryKeys.quotesBasicList(), params],
    queryFn: () =>
      apiGet<ApiListResponse<IQuoteBasicRespSchema>>('/api/quotes', {
        params,
      }).then((res) => res.data),
    ...options,
  });
}

/**
 * Fetch latest processed quote for a bill group id.
 *
 * Composes a list-by-billGroup query with a get-by-id query. We need the
 * second hop because the list endpoint doesn't return the full quote shape.
 *
 * Loading/fetching state is merged across both hops so callers see a single
 * accurate signal — the list phase contributes its own pending state, not
 * just the byId phase.
 */
export function useGetLatestQuoteByBillGroupId(
  {
    billGroupId,
  }: {
    billGroupId: string;
  },
  options: { enabled?: boolean } = {},
) {
  const enabledOption = options.enabled ?? true;

  const {
    data: quoteId,
    isLoading: isListLoading,
    isFetching: isListFetching,
    error: listError,
  } = useGetQuoteList(
    {
      config: {
        rows: 1,
        sortField: 'createDate',
        sortOrder: -1,
      },
      filters: {
        billGroupId,
        status: QuoteStatusEnum.PROCESSED,
      },
    },
    {
      enabled: !!billGroupId && enabledOption,
      select: (data) => data?.content?.[0]?.id,
    },
  );

  // Gate the byId fetch on the list query being settled so that a stale
  // quoteId from a previous billGroupId can't briefly resolve against the
  // new billGroupId while the list refetch is in flight.
  const byIdResult = useGetById<IQuoteRespSchema>(
    'cpqServiceQuotes',
    quoteId ?? '',
    {
      enabled: !!quoteId && !isListFetching && enabledOption,
    },
  );

  return {
    ...byIdResult,
    isLoading: isListLoading || byIdResult.isLoading,
    isFetching: isListFetching || byIdResult.isFetching,
    error: listError ?? byIdResult.error,
  };
}

export async function doDeleteQuote(quoteId: string): Promise<any> {
  const response = await apiDelete<any>(`/api/quotes/${quoteId}`);
  return response.data;
}

// Ends

// subscriptions
export async function doGetSubscriptionPrices(
  signal: AbortSignal,
  payload: ISubscriptionsFormReq,
): Promise<ISubscriptionListPricingResponseSchema> {
  const res = await apiPost<ISubscriptionListPricingResponseSchema>(
    `/api/subscriptions/pricing`,
    payload,
    {
      signal,
    },
  );
  return res.data;
}

// quote apis
export async function doCreateQuote(
  payload: IQuoteRequestSchema,
): Promise<IQuoteRespSchema> {
  const res = await apiPost<any>(`/api/quotes`, payload);
  return res.data;
}

export async function doGetQuote(quoteId: string): Promise<IQuoteRespSchema> {
  const res = await api.get<IQuoteRespSchema>(`/api/quotes/${quoteId}`);
  return sortQuoteItems(res.data);
}

export async function doReviewQuote(
  quoteId: string,
  data?: IQuoteReviewReq,
): Promise<IQuoteRespSchema> {
  const res = await apiPost<IQuoteRespSchema>(
    `/api/quotes/${quoteId}/review`,
    data || {},
  );
  return sortQuoteItems(res.data);
}

export async function doUpdateQuote(
  quoteId: string,
  payload: IQuoteRequestSchema,
): Promise<IQuoteRespSchema> {
  const res = await apiPut<IQuoteRespSchema>(`/api/quotes/${quoteId}`, payload);
  return sortQuoteItems(res.data);
}
export async function doUpdateQuoteCollaborationAccess(
  quoteId: string,
  payload: { collaborationAccess: CollaborationAccessEnum },
) {
  await apiPut(`/api/quotes/${quoteId}/collaborationAccess`, payload);
}

export async function doEvaluateQuoteRules(
  quoteId: string,
): Promise<IQuoteRespSchema> {
  const res = await apiPost<IQuoteRespSchema>(
    `/api/quotes/${quoteId}/evaluateRules`,
  );
  return sortQuoteItems(res.data);
}

export async function doSendQuote(quoteId: string): Promise<IQuoteRespSchema> {
  const res = await apiPost<IQuoteRespSchema>(`/api/quotes/${quoteId}/send`);
  return sortQuoteItems(res.data);
}

export async function doEditQuote(
  quoteId: string,
  extraData?: QuoteEditRequest,
): Promise<IQuoteRespSchema> {
  const res = await apiPost<IQuoteRespSchema>(
    `/api/quotes/${quoteId}/edit`,
    extraData,
  );
  return sortQuoteItems(res.data);
}

export async function doRecreateQuote(
  quoteId: string,
): Promise<IQuoteRespSchema> {
  const res = await apiPost<IQuoteRespSchema>(
    `/api/quotes/${quoteId}/recreate`,
  );
  return sortQuoteItems(res.data);
}

export async function doReorderQuoteOfferings(
  quoteId: string,
  payload: ReOrderRequestTypes,
): Promise<IQuoteRespSchema> {
  const res = await apiPut<IQuoteRespSchema>(
    `/api/quotes/${quoteId}/quoteOfferings/reorder`,
    payload,
  );
  return sortQuoteItems(res.data);
}

export function useChangeQuoteOwner(
  options: Partial<
    UseMutationOptions<
      IQuoteRespSchema,
      unknown,
      {
        quoteId: string;
        ownerId: string;
      }
    >
  > = {},
) {
  return useMutation<
    IQuoteRespSchema,
    unknown,
    {
      quoteId: string;
      ownerId: string;
    }
  >({
    mutationFn: ({ quoteId, ownerId }) =>
      enqueueForQuote(quoteId, () =>
        apiPost<IQuoteRespSchema>(
          `/api/quotes/${quoteId}/changeOwner/${ownerId}`,
        ).then((res) => sortQuoteItems(res.data)),
      ),
    ...options,
  });
}

export function useGetQuoteBillingSchedule(
  quoteId: string,
  version: 'V1' | 'V2',
  additionalQueryKeys: unknown[] = [],
  options: Partial<UseQueryOptions<IQuoteBillingScheduleRespSchema>> = {},
) {
  const url =
    version === 'V1'
      ? `/api/quotes/${quoteId}/billingSchedule`
      : `/api/quotes/${quoteId}/billingSchedule/diff`;
  return useQuery({
    queryKey: [
      ...cpqServiceQueryKeys.quoteBillingSchedule(quoteId),
      version,
      ...additionalQueryKeys,
    ],
    queryFn: () =>
      apiGet<IQuoteBillingScheduleRespSchema>(url).then((res) => res.data),
    enabled: !!quoteId,
    refetchOnWindowFocus: false,
    ...options,
  });
}

// Quotes Api

export async function doGetQuotesByAccount(
  accountId: string,
  config: GetListApiConfig,
  filters?: GetListApiFilter,
) {
  const params = composeGetQuery(config, filters);
  const res = await apiGet<ApiListResponse<IQuoteRespSchema>>(
    `/api/accounts/${accountId}/quotes`,
    { params },
  );
  return res.data;
}

export async function getContactByQuote(
  quoteId: string,
): Promise<IQuoteContacts> {
  const res = await apiGet<IQuoteContacts>(`/api/quotes/${quoteId}/contacts`);
  return res.data;
}

/** TODO: deprecate in favor of useUpdateContact from accountService and extend to handle both actions */
export async function updateContactByQuote(
  quoteId: string,
  payload: IQuoteContactsRequest,
): Promise<IQuoteContacts> {
  const res = await apiPut<IQuoteContacts>(
    `/api/quotes/${quoteId}/contacts`,
    payload,
  );
  return res.data;
}

export async function updateQuoteAddress(
  quoteId: string,
  payload: IQuoteContactAddressDataSchema,
): Promise<IQuoteContactAddressDataSchema> {
  const res = await apiPut<IQuoteContactAddressDataSchema>(
    `/api/quotes/${quoteId}/addresses`,
    payload,
  );
  return res.data;
}

export async function doChangeQuoteSigningOrder(
  quoteId: string,
  signingOrder: SigningOrderEnum,
): Promise<IQuoteSigningOrderResp> {
  const res = await apiPut<IQuoteSigningOrderResp>(
    `/api/quotes/${quoteId}/signingConfig`,
    {
      signingOrder,
    },
  );
  return res.data;
}

export function useGetEsignRecipients(
  quoteId: string,
  options: Partial<UseQueryOptions<ESignRecipientResponse>> = {},
) {
  return useQuery({
    queryKey: [...cpqServiceQueryKeys.esignRecipients(quoteId)],
    queryFn: () =>
      apiGet<ESignRecipientResponse>(
        `/api/quotes/${quoteId}/esignRecipients`,
      ).then((res) => res.data),
    refetchOnWindowFocus: false,
    ...options,
  });
}

export async function doManuallyAcceptQuote(
  quoteId: string,
  data?: FormData,
): Promise<IQuoteRespSchema> {
  const config: MAxiosRequestConfig = {};
  if (data) {
    config.headers = new AxiosHeaders({
      'Content-Type': 'multipart/form-data',
    });
  }

  const res = await apiPost<IQuoteRespSchema>(
    `/api/quotes/${quoteId}/accept`,
    data || {},
    config,
  );
  return sortQuoteItems(res.data);
}

export async function doProcessQuote(
  quoteId: string,
): Promise<IQuoteRespSchema> {
  await apiPost<IQuoteRespSchema>(`/api/quotes/${quoteId}/process`);
  return doGetQuote(quoteId);
}

export async function doSetQuoteToAdminEditableStatus(
  quoteId: string,
): Promise<IQuoteRespSchema> {
  await apiPost<IQuoteRespSchema>(`/api/quotes/${quoteId}/accepted/edit`);
  return doGetQuote(quoteId);
}

export async function doUpdateDelayedBilling(
  quoteId: string,
  startDate: string,
): Promise<IQuoteRespSchema> {
  const res = await apiPost<IQuoteRespSchema>(
    `/api/quotes/${quoteId}/delayedBilling`,
    {
      startDate,
    },
  );
  return sortQuoteItems(res.data);
}

export async function doCancelQuoteService(
  quote: IQuoteBasicRespSchema & Partial<IQuoteRespSchema>,
  reason: QuoteCancelationReasonEnum,
): Promise<IQuoteRespSchema> {
  const res = await apiPost<IQuoteRespSchema>(
    `/api/quotes/${quote.id}/cancel`,
    quote,
    {
      params: { reason },
    },
  );
  return sortQuoteItems(res.data);
}

export function useResendQuoteEsign(
  options: Partial<
    UseMutationOptions<unknown, unknown, { quoteId: string }>
  > = {},
) {
  const { onSuccess, ...restOptions } = options;
  return useMutation<unknown, unknown, { quoteId: string }>({
    mutationFn: ({ quoteId }) =>
      enqueueForQuote(quoteId, () =>
        apiPost(`/api/quotes/${quoteId}/esign/resend`),
      ),
    ...restOptions,
  });
}

export function useUpdateQuoteEsignRequirement(
  options: Partial<
    UseMutationOptions<
      void,
      unknown,
      { quoteId: string; requiresEsign: boolean }
    >
  > = {},
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;
  return useMutation<
    void,
    unknown,
    { quoteId: string; requiresEsign: boolean }
  >({
    mutationFn: ({ quoteId, requiresEsign }) =>
      enqueueForQuote(quoteId, () =>
        apiPut<void>(`/api/quotes/${quoteId}/esign`, { requiresEsign }).then(
          (res) => res.data,
        ),
      ),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: cpqServiceQueryKeys.quotesById(variables.quoteId),
      });
    },
    ...restOptions,
  });
}

export async function doRetractQuoteService(
  quoteId: string,
): Promise<IQuoteRespSchema> {
  const res = await apiPost<IQuoteRespSchema>(`/api/quotes/${quoteId}/retract`);
  return sortQuoteItems(res.data);
}

export async function doArchiveQuoteService(quoteId: string) {
  await apiPut(`/api/quotes/${quoteId}/archive`);
}

/**
 * Allows adding multiple quote offerings (serially) to a quote and returns the result from the last call
 */
export function useBulkAddOfferingsToQuote(
  options: Partial<
    UseMutationOptions<
      IQuoteRespSchema,
      unknown,
      {
        quoteId: string;
        quoteOfferings: IQuoteOfferingReqSchema[];
      }
    >
  > = {},
) {
  const { onSuccess, ...restOptions } = options;
  return useMutation<
    IQuoteRespSchema,
    unknown,
    {
      quoteId: string;
      quoteOfferings: IQuoteOfferingReqSchema[];
    }
  >({
    mutationFn: ({ quoteId, quoteOfferings }) => {
      return (async () => {
        let latestQuote: IQuoteRespSchema | null = null;
        for (const quoteOffering of quoteOfferings) {
          latestQuote = await enqueueForQuote(quoteId, () =>
            apiPost(
              `/api/quotes/${quoteId}/quoteOfferings`,
              quoteOffering,
            ).then((res) => res.data),
          );
        }
        if (!latestQuote) {
          throw new Error('Invalid payload');
        }
        return sortQuoteItems(latestQuote);
      })();
    },
    onSuccess: (data, variables, context) => {
      onSuccess && onSuccess(data, variables, context);
    },
    ...restOptions,
  });
}

export async function doCreateQuoteOffering(
  quoteId: string,
  payload: IQuoteOfferingReqSchema,
): Promise<IQuoteOfferingRespSchema> {
  const res = await apiPost<IQuoteOfferingRespSchema>(
    `/api/quotes/${quoteId}/quoteOfferings`,
    payload,
    { axiosConfig: { retryOnConflict: true } },
  );
  // Ensure quoteItems are sorted by product type so components do not need to worry about sorting
  res.data.items = sortByProductType(res.data.items);
  return res.data;
}

export async function doUpdateQuoteOffering(
  quoteId: string,
  quoteOfferingId: string,
  payload: IQuoteOfferingReqSchema,
): Promise<IQuoteOfferingRespSchema> {
  const res = await apiPut<IQuoteOfferingRespSchema>(
    `/api/quotes/${quoteId}/quoteOfferings/${quoteOfferingId}`,
    payload,
    { axiosConfig: { retryOnConflict: true } },
  );
  // Ensure quoteItems are sorted by product type so components do not need to worry about sorting
  res.data.items = sortByProductType(res.data.items);
  return res.data;
}

export async function doRemoveQuoteOffering(
  quoteId: string,
  payload: IQuoteOfferingRemoveReqSchema,
): Promise<IQuoteRespSchema> {
  const res = await apiPost<IQuoteRespSchema>(
    `/api/quotes/${quoteId}/quoteOfferings/remove`,
    payload,
  );
  return sortQuoteItems(res.data);
}

export async function doRevertQuoteOffering(
  quoteId: string,
  quoteOfferingId: string,
): Promise<IQuoteRespSchema> {
  const res = await apiPost<IQuoteRespSchema>(
    `/api/quotes/${quoteId}/quoteOfferings/${quoteOfferingId}/revert`,
  );
  return sortQuoteItems(res.data);
}

export const doUploadQuoteDocument = async (
  quoteId: string,
  file: File,
  type = StorageTypeEnum.MSA,
) => {
  const formData = new FormData();
  formData.append('file', file);

  const res = await apiUpload<IStorage>(
    `/api/quotes/${quoteId}/upload?type=${type}`,
    formData,
  );
  return res.data;
};

export function useGetQuoteDisplayConfig(
  quoteId: string,
  options: Partial<UseQueryOptions<IQuoteTemplateConfigSchema>> = {},
) {
  return useQuery({
    queryKey: [...cpqServiceQueryKeys.displayConfig(quoteId)],
    queryFn: () =>
      apiGet<IQuoteTemplateConfigSchema>(
        `/api/quotes/${quoteId}/displayConfig`,
      ).then((res) => res.data),
    refetchOnWindowFocus: false,
    ...options,
  });
}

export function useUpdateQuoteDisplayConfig(
  options: Partial<
    UseMutationOptions<
      IQuoteTemplateConfigSchema,
      unknown,
      {
        quoteId: string;
        payload: IQuoteTemplateConfigSchema;
      }
    >
  > = {},
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;
  return useMutation<
    IQuoteTemplateConfigSchema,
    unknown,
    {
      quoteId: string;
      payload: IQuoteTemplateConfigSchema;
    }
  >({
    mutationFn: ({ quoteId, payload }) =>
      enqueueForQuote(quoteId, () =>
        apiPut<IQuoteTemplateConfigSchema>(
          `/api/quotes/${quoteId}/displayConfig`,
          {
            defaultTemplate: 'QUOTE_DEFAULT_1', // FALLBACK TO DEFAULT TEMPLATE UNTIL BE HAS THIS DATA
            ...payload,
          },
        ).then((res) => res.data),
      ),
    onSuccess: (data, variables, context) => {
      queryClient.setQueryData(
        [...cpqServiceQueryKeys.displayConfig(variables.quoteId)],
        data,
      );
      onSuccess && onSuccess(data, variables, context);
    },
    ...restOptions,
  });
}

export function useExtendExpirationDate(
  options: Partial<
    UseMutationOptions<
      IQuoteRespSchema,
      unknown,
      {
        quoteId: string;
        payload: IExtendedExpirationDateReqSchema;
      }
    >
  > = {},
) {
  return useMutation<
    IQuoteRespSchema,
    unknown,
    {
      quoteId: string;
      payload: IExtendedExpirationDateReqSchema;
    }
  >({
    mutationFn: ({ quoteId, payload }) =>
      enqueueForQuote(quoteId, () =>
        apiPut<IQuoteRespSchema>(
          `/api/quotes/${quoteId}/extendExpirationDate`,
          undefined,
          { params: payload },
        ).then((res) => sortQuoteItems(res.data)),
      ),
    ...options,
  });
}

export function useUpdateNewQuoteType(
  options: Partial<
    UseMutationOptions<
      IQuoteRespSchema,
      unknown,
      {
        quoteId: string;
        payload: INewQuoteTypeReqSchema;
      }
    >
  > = {},
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation<
    IQuoteRespSchema,
    unknown,
    {
      quoteId: string;
      payload: INewQuoteTypeReqSchema;
    }
  >({
    mutationFn: ({ quoteId, payload }) =>
      enqueueForQuote(quoteId, () =>
        apiPut<IQuoteRespSchema>(
          `/api/quotes/${quoteId}/newQuoteType`,
          payload,
        ).then((res) => sortQuoteItems(res.data)),
      ),
    onSuccess: (data, variables, context) => {
      const existingQuote = queryClient.getQueryData<IQuoteRespSchema>(
        cpqServiceQueryKeys.quotesById(variables.quoteId),
      );

      let mergedQuote = data;
      if (existingQuote?.metadata?.bluePrintId) {
        mergedQuote = {
          ...data,
          metadata: {
            ...data.metadata,
            bluePrintId:
              data.metadata?.bluePrintId ?? existingQuote.metadata.bluePrintId,
          },
        };
      }

      queryClient.setQueryData(
        cpqServiceQueryKeys.quotesById(variables.quoteId),
        mergedQuote,
      );

      if (onSuccess) {
        onSuccess(mergedQuote, variables, context);
      }
    },
    ...restOptions,
  });
}

// OPPORTUNITIES
export function useGetOpportunities<
  T = ApiListResponse<IOpportunitySummary>,
  SelectData = ApiListResponse<IOpportunitySummary>,
>(
  {
    config,
    filters,
    url = '/api/opportunities',
  }: {
    config: GetListApiConfig;
    filters?: GetListApiFilter;
    url?: string;
  },
  options: Partial<UseQueryOptions<T, unknown, SelectData>> = {},
) {
  const params = composeGetQuery(config, filters);
  return useQuery({
    queryKey: [...cpqServiceQueryKeys.opportunityList(), url, params],
    queryFn: () =>
      apiGet<T>(url, {
        params,
      }).then((res) => res.data),
    ...options,
  });
}

export function useGetOpportunityById(
  opportunityId: string,
  options: Partial<UseQueryOptions<IOpportunityWithQuotes>> = {},
) {
  return useQuery<IOpportunityWithQuotes>({
    queryKey: cpqServiceQueryKeys.opportunityById(opportunityId),
    queryFn: () =>
      apiGet<IOpportunityWithQuotes>(`/api/opportunities/${opportunityId}`)
        .then((res) => res.data)
        .then((opportunity) => ({
          ...opportunity,
          quotes: orderObjectsBy(
            opportunity.quotes || [],
            ['modifyDate'],
            ['desc', 'desc'],
          ),
        })),
    ...options,
  });
}

/**
 * Update customId on Opportunity
 */
export function useLinkOpportunityToCrm(
  options: Partial<
    UseMutationOptions<
      IOpportunityWithQuotes,
      unknown,
      { opportunityId: string; customId: string | null }
    >
  > = {},
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;
  return useMutation<
    IOpportunityWithQuotes,
    unknown,
    { opportunityId: string; customId: string | null }
  >({
    mutationFn: ({ opportunityId, customId }) =>
      apiPut<IOpportunityWithQuotes>(`/api/opportunities/${opportunityId}`, {
        customId,
      }).then((res) => res.data),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: cpqServiceQueryKeys.opportunityById(data.id),
      });
      data.quotes?.map((quote) =>
        queryClient.invalidateQueries({
          queryKey: [...cpqServiceQueryKeys.quotesById(quote.id)],
        }),
      );
      onSuccess && onSuccess(data, variables, context);
    },
    ...restOptions,
  });
}

// /api/opportunities/customId/{id}
export function getOpportunityByCustomId(customId: string) {
  return apiGet<IOpportunityWithQuotes>(
    `/api/opportunities/customId/${customId}`,
  ).then((res) => res.data);
}

export const doGetOrCreateOpportunity = async (
  customId: string,
  createPayload: any,
) => {
  return getOpportunityByCustomId(customId).catch((ex) =>
    apiPost(
      `/api/opportunities`,
      OpportunityReqSchema.parse(createPayload),
    ).then((res) => res.data),
  );
};

/**
 * If an opportunity with the customId exists, then use it, otherwise create a new opportunity
 */
export function useGetOrCreateOpportunityWithCrmLink(
  options: Partial<
    UseMutationOptions<
      IOpportunityWithQuotes,
      unknown,
      {
        customId: string;
        createPayload: IOpportunityReqSchema;
      }
    >
  > = {},
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;
  return useMutation<
    IOpportunityWithQuotes,
    unknown,
    {
      customId: string;
      createPayload: IOpportunityReqSchema;
    }
  >({
    mutationFn: ({ createPayload, customId }) => {
      return getOpportunityByCustomId(customId)
        .then((opportunity) => opportunity)
        .catch((ex) =>
          apiPost(
            `/api/opportunities`,
            OpportunityReqSchema.parse(createPayload),
          ).then((res) => res.data),
        );
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: cpqServiceQueryKeys.opportunityList(),
      });
      queryClient.setQueryData(
        cpqServiceQueryKeys.opportunityById(data.id),
        variables,
      );
      onSuccess && onSuccess(data, variables, context);
    },
    ...restOptions,
  });
}

export function useUpdateOpportunityQuote(
  options: Partial<
    UseMutationOptions<
      void,
      unknown,
      {
        opportunityId: string;
        quoteId: string;
      }
    >
  > = {},
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;
  return useMutation<
    void,
    unknown,
    {
      opportunityId: string;
      quoteId: string;
    }
  >({
    mutationFn: ({ opportunityId, quoteId }) =>
      apiPut(
        `/api/opportunities/${opportunityId}/quotes/${quoteId}/setPrimary`,
      ).then((res) => res.data),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: cpqServiceQueryKeys.opportunityList(),
      });
      queryClient.invalidateQueries({
        queryKey: cpqServiceQueryKeys.opportunityById(variables.opportunityId),
      });
      onSuccess && onSuccess(data, variables, context);
    },
    ...restOptions,
  });
}

// CONTRACTS
export function useGetContracts<
  T = ApiListResponse<IContract>,
  SelectData = ApiListResponse<IContract>,
>(
  {
    config,
    filters,
    url = '/api/contracts',
  }: {
    config: GetListApiConfig;
    filters?: GetListApiFilter;
    url?: string;
  },
  options: Partial<UseQueryOptions<T, unknown, SelectData>> = {},
) {
  const params = composeGetQuery(config, filters);
  return useQuery({
    queryKey: [...cpqServiceQueryKeys.contractList(), url, params],
    queryFn: () =>
      apiGet<T>(url, {
        params,
      }).then((res) => res.data),
    ...options,
  });
}

export function useUpdateContractById(
  options: Partial<
    UseMutationOptions<
      IContractWithQuotes,
      unknown,
      { id: string; payload: { endAction: ContractEndActionEnum } }
    >
  > = {},
) {
  const { onSuccess, ...restOptions } = options;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }) =>
      apiPut<IContractWithQuotes>(`/api/contracts/${id}`, payload).then(
        (res) => res.data,
      ),
    onSuccess: (data, variables, context) => {
      updateListCacheWithUpdatedItem(
        queryClient,
        [...cpqServiceQueryKeys.contractList()],
        data,
        true,
      );
      queryClient.invalidateQueries({
        queryKey: [...cpqServiceQueryKeys.contractById(data.id)],
      });
      queryClient.invalidateQueries({
        queryKey: cpqServiceQueryKeys.accountContractsAll(),
      });
      onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
}
/**
 * Every contract on the account, oldest first. Used to build lineages, which
 * need the whole chain rather than one page of the table.
 */
export function useGetAllAccountContracts<SelectData = IContract[]>(
  accountId: string,
  options: Partial<UseQueryOptions<IContract[], unknown, SelectData>> = {},
) {
  return useQuery({
    queryKey: cpqServiceQueryKeys.accountContractsAllByAccount(accountId),
    queryFn: ({ signal }) =>
      apiGetAllList<IContract>(`/api/accounts/${accountId}/contracts`, {
        rows: 100,
        sortField: 'startDate',
        sortOrder: 1,
        signal,
      }),
    enabled: !!accountId,
    ...options,
  });
}

export async function doGetContractsByAccount(
  accountId: string,
  config: GetListApiConfig,
  filters?: GetListApiFilter,
): Promise<ApiListResponse<IContract>> {
  const params = composeGetQuery(config, filters);
  const res = await apiGet<ApiListResponse<IContract>>(
    `/api/accounts/${accountId}/contracts`,
    { params },
  );
  return res.data;
}

export async function doGetContract(
  contractId: string,
): Promise<IContractWithQuotes> {
  const res = await apiGet<IContractWithQuotes>(`/api/contracts/${contractId}`);
  return res.data;
}

export function useGetContractById(
  contractId: string,
  options: Partial<UseQueryOptions<IContractWithQuotes>> = {},
) {
  return useQuery<IContractWithQuotes>({
    queryKey: cpqServiceQueryKeys.contractById(contractId),
    queryFn: () =>
      apiGet<IContractWithQuotes>(`/api/contracts/${contractId}`).then(
        (res) => res.data,
      ),
    ...options,
  });
}

export async function doAmendContract(
  contractId: string,
  opportunity?: Partial<Pick<IOpportunitySummary, 'id' | 'name' | 'customId'>>,
  amendmentDate?: Maybe<string>,
  additionalFields?: QuoteAdditionalFields,
) {
  // Build the payload with both opportunity and quoteCustomization if needed
  let payload: {
    opportunity?: Partial<
      Pick<IOpportunitySummary, 'id' | 'name' | 'customId'>
    >;
  } & QuoteAdditionalFields = {};

  if (opportunity?.id || (opportunity?.customId && opportunity?.name)) {
    payload.opportunity = opportunity;
  }

  if (additionalFields) {
    payload = {
      ...payload,
      ...additionalFields,
    };
  }

  const res = await apiPost<IQuoteRespSchema>(
    `/api/contracts/${contractId}/amend`,
    payload,
    { params: { amendmentDate } },
  );
  return sortQuoteItems(res.data);
}

export async function doGetBillingScheduleByContractId(
  contractId: string,
): Promise<IBillingScheduleRespSchemaUI> {
  const res = await apiGet<IBillingScheduleRespSchemaUI>(
    `/api/contracts/${contractId}/billingSchedule`,
  );
  return {
    ...res.data,
    usageProductExists: res.data.periods.some(({ items }) =>
      items.some(({ productType }) => productType === ProductTypeEnum.USAGE),
    ),
    periods: res.data.periods.map((data, index) => {
      const periodUId = data.invoiceId
        ? data.invoiceId
        : `${data.invoiceDate}${index}`;
      return {
        ...data,
        uid: periodUId,
        items: data.items.map((item, index) => ({
          ...item,
          uid: `${periodUId}${item.pricePerUnit}${index}`,
        })),
        usageProductExists: data.items.some(
          ({ productType }) => productType === ProductTypeEnum.USAGE,
        ),
      };
    }),
  };
}

/**
 * Performs multiple API requests to cancel a contract
 * Amend contract as of specified date
 */
export function useCancelContract() {
  const queryClient = useQueryClient();

  return useMutation<
    IQuoteRespSchema,
    unknown,
    {
      contractId: string;
      amendmentEffectiveDate: string;
      ownerId: string;
      additionalFields?: QuoteAdditionalFields;
    }
  >({
    mutationFn: async ({
      amendmentEffectiveDate,
      contractId,
      ownerId,
      additionalFields,
    }) => {
      // Firstly Amend the contract to get the amended quote response
      let quote = await doAmendContract(
        contractId,
        undefined,
        amendmentEffectiveDate,
        additionalFields,
      );
      queryClient.setQueryData(cpqServiceQueryKeys.quotesById(quote.id), quote);

      // Update the quote description to reflect the cancellation
      quote = await doUpdateQuote(
        quote.id,
        getQuoteUpdateRequestFromQuote(quote, {
          description: `Cancel Contract for ${quote.accountName}`,
        }),
      );
      queryClient.setQueryData(cpqServiceQueryKeys.quotesById(quote.id), quote);

      for (const quoteOffering of quote.quoteOfferings) {
        // skip child offerings
        if (quoteOffering.parentQuoteOfferingId) {
          continue;
        }

        // skip onetime offerings that are effective prior to the cancellation date
        if (
          quoteOffering.billingFrequency === RateBillingFrequencyEnum.ONETIME &&
          isBefore(
            parseISO(quoteOffering.startDate),
            parseISO(amendmentEffectiveDate),
          )
        ) {
          continue;
        }

        // Skip onetime offerings that were already part of the amendment and had no subscriptionId
        const isOnetimeOfferingOnAmendmentThatAlreadyExists =
          quoteOffering.items.every(
            ({ productType, amendmentStatus }) =>
              (productType === ProductTypeEnum.ONETIME ||
                productType === ProductTypeEnum.ONETIME_PREPAID_CREDIT) &&
              amendmentStatus !== QuoteItemAmendmentStatusEnum.ADDED &&
              isNull(quoteOffering.subscriptionId),
          );
        if (isOnetimeOfferingOnAmendmentThatAlreadyExists) {
          continue;
        }

        // deletion effective date
        let newContractEndDate = formatISO(
          addDays(parseISO(amendmentEffectiveDate), -1),
          { representation: 'date' },
        );

        // For one-time offerings, set the cancellation end_date to one day before the start date
        if (
          quoteOffering?.billingFrequency === RateBillingFrequencyEnum.ONETIME
        ) {
          newContractEndDate = formatISO(
            addDays(parseISO(quoteOffering.startDate), -1),
            { representation: 'date' },
          );
        }

        // Offering is eligible for cancellation
        await doRemoveQuoteOffering(quote.id, {
          removal_scope: QuoteOfferingRemovalScopeEnum.GROUP,
          group_removal_mode: QuoteOfferingGroupRemovalModeEnum.TARGET_DATE,
          group_id: quoteOffering.id,
          end_date: newContractEndDate,
        });
      }

      // Fetch the latest state of the quote and add to query cache
      quote = await doGetQuote(quote.id);
      queryClient.setQueryData(cpqServiceQueryKeys.quotesById(quote.id), quote);

      return quote;
    },
  });
}

export async function doRenewContract(
  contractId: string,
  options?: {
    contractRenewalOptions?: IContractRenewalReqSchema;
    opportunity?: Partial<
      Pick<IOpportunitySummary, 'id' | 'name' | 'customId'>
    >;
    additionalFields?: QuoteAdditionalFields;
  },
) {
  // optionally allow providing existing fields on the opportunity that will be created
  let payload: IContractRenewalReqSchema = {};

  if (options) {
    const { opportunity, additionalFields, contractRenewalOptions } = options;

    if (
      opportunity &&
      (opportunity.id || (opportunity.customId && opportunity.name))
    ) {
      payload.opportunity = opportunity;
    }

    if (contractRenewalOptions) {
      payload = { ...payload, ...contractRenewalOptions };
    }

    if (additionalFields) {
      payload = { ...payload, ...additionalFields };
    }
  }

  const res = await apiPost<IQuoteRespSchema>(
    `/api/contracts/${contractId}/renew`,
    payload,
  );
  return sortQuoteItems(res.data);
}

export function useChangeContractOwner(
  options: Partial<
    UseMutationOptions<
      IContractWithQuotes,
      unknown,
      {
        contractId: string;
        userId: string;
      }
    >
  > = {},
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;
  return useMutation<
    IContractWithQuotes,
    unknown,
    {
      contractId: string;
      userId: string;
    }
  >({
    mutationFn: ({ contractId, userId }) =>
      apiPost(`/api/contracts/${contractId}/changeOwner/${userId}`).then(
        (res) => res.data,
      ),
    onSuccess: (data, variables, context) => {
      queryClient.setQueryData(
        cpqServiceQueryKeys.contractById(variables.contractId),
        data,
      );
      updateListCacheWithUpdatedItem(
        queryClient,
        cpqServiceQueryKeys.contractList(),
        data,
      );

      onSuccess && onSuccess(data, variables, context);
    },
    ...restOptions,
  });
}

// Storages
export const doGetQuoteStorageById = async (
  quoteId: string,
  storageId: string,
  params?: any,
) => {
  const res = await apiGet<IStorage>(
    `/api/quotes/${quoteId}/storages/${storageId}`,
    {
      params,
    },
  );
  return res.data;
};

export function useGetQuoteStorageById(
  {
    quoteId,
    storageId,
    params,
  }: {
    quoteId: string;
    storageId: string;
    params?: any;
  },
  options: Partial<UseQueryOptions<IStorage>> = {},
) {
  return useQuery({
    queryKey: [
      ...cpqServiceQueryKeys.quoteStorageById(quoteId, storageId),
      params,
    ],
    queryFn: () =>
      apiGet<IStorage>(`/api/quotes/${quoteId}/storages/${storageId}`, {
        params,
      }).then((res) => res.data),
    refetchOnWindowFocus: false,
    // 10 minutes
    gcTime: 1000 * 60 * 10,
    // 5 minutes
    staleTime: 1000 * 60 * 5,
    ...options,
  });
}

export const doDeleteQuoteStorageById = async (
  quoteId?: string,
  storageId?: string,
  params?: { type: StorageTypeEnum },
) => {
  await apiDelete<void>(`/api/quotes/${quoteId}/storages/${storageId}`, {
    params,
  });
};

export const doPrintQuoteToPdf = async (
  quoteId: string,
  getSignedIfAvailable = true,
  quoteOnly = false,
) => {
  const res = await apiGet<ArrayBuffer>(
    `/api/quotes/${quoteId}/print?getSignedIfAvailable=${getSignedIfAvailable}&quoteOnly=${quoteOnly}`,
    {
      responseType: 'arraybuffer',
      headers: new AxiosHeaders({
        accept: 'application/pdf',
      }),
    },
  );
  return res.data;
};

/**
 * Word (.docx) rendition of the quote, converted server-side from the same
 * `/print` output. There is no `getSignedIfAvailable` equivalent: the backend
 * refuses a quote that already has a signed document, so callers must gate on
 * that before offering the download.
 */
export const doPrintQuoteToDocx = async (
  quoteId: string,
  quoteOnly = false,
) => {
  const res = await apiGet<ArrayBuffer>(
    `/api/quotes/${quoteId}/print?quoteOnly=${quoteOnly}`,
    {
      responseType: 'arraybuffer',
      headers: new AxiosHeaders({
        accept: MimeTypeEnum.DOCX,
      }),
    },
  );
  return res.data;
};

export const doPrintQuoteToHtml = async (quoteId: string) => {
  const htmlData = await apiGet<string>(`/api/quotes/${quoteId}/print`, {
    responseType: 'text',
    headers: new AxiosHeaders({
      accept: 'text/html',
    }),
  });
  return htmlData.data;
};

export const usePrintQuoteToHtml = (
  {
    quoteId,
    displayConfig,
    cacheKeys = [],
  }: {
    quoteId: string;
    /** Used for caching response and to trigger re-fetch */
    displayConfig?: IQuoteTemplateConfigSchema;
    /** Used for caching response */
    cacheKeys?: string[];
  },
  options: Partial<UseQueryOptions<string>> = {},
) => {
  return useQuery({
    queryKey: [
      ...cpqServiceQueryKeys.quoteTemplateHtml(quoteId),
      displayConfig,
      ...cacheKeys,
    ],
    queryFn: () =>
      apiGet<string>(`/api/quotes/${quoteId}/print`, {
        responseType: 'text',
        headers: new AxiosHeaders({
          accept: 'text/html',
        }),
      }).then((res) => res.data),
    refetchOnWindowFocus: false,
    retry: false,
    placeholderData: (previousData, previousQuery) => previousData,
    // 10 minutes
    gcTime: 1000 * 60 * 10,
    // 5 minutes
    staleTime: 1000 * 60 * 5,
    ...options,
  });
};

export const doCloneQuote = async (quoteId: string) => {
  const res = await apiPost<IQuoteRespSchema>(`/api/quotes/${quoteId}/copy`);
  return res.data;
};

export function useGetQuoteDiscounts(
  {
    quoteId,
    queryKeys = [],
  }: {
    quoteId: string;
    /** Optional additional query keys to add for caching */
    queryKeys?: unknown[];
  },
  options: Partial<UseQueryOptions<IQuoteDiscounts>> = {},
) {
  return useQuery({
    queryKey: [...cpqServiceQueryKeys.quoteDiscounts(quoteId), ...queryKeys],
    queryFn: () =>
      apiGet<IQuoteDiscounts>(`/api/quotes/${quoteId}/discounts`).then(
        (res) => res.data,
      ),
    refetchOnWindowFocus: false,
    // 5 minutes
    staleTime: 1000 * 60 * 5,
    ...options,
  });
}

export function useGetQuotePrices(
  quoteId: string,
  options: Partial<UseQueryOptions<IQuotePrice[]>> = {},
) {
  return useQuery({
    queryKey: [...cpqServiceQueryKeys.quotePrices(quoteId)],
    queryFn: () =>
      apiGet<{ quoteOfferingPrices: IQuotePrice[] }>(
        `/api/quotes/${quoteId}/prices`,
        { params: { applyDisplayConfiguration: true } },
      ).then((res) =>
        (res.data?.quoteOfferingPrices || []).map(
          (quotePrice: IQuotePrice): IQuotePrice => {
            return {
              ...quotePrice,
              schedule: quotePrice.schedule.map((scheduleItem) => {
                return {
                  ...scheduleItem,
                  productPrices: sortByProductObjType(
                    scheduleItem.productPrices,
                    'description',
                  ),
                };
              }),
            };
          },
        ),
      ),
    refetchOnWindowFocus: false,
    ...options,
  });
}

export const doUpdateQuoteConditionalTerm = async (
  quoteId: string,
  conTermId: string,
  payload: { terms: string },
) => {
  const resp = await apiPut<IQuoteConditionalTermSchema>(
    `/api/quotes/${quoteId}/conditionalTerms/${conTermId}`,
    payload,
    {
      axiosConfig: {
        skipNullifyStrings: true,
      },
    },
  );
  return resp.data;
};

export const doRevertQuoteConditionalTerm = async (
  quoteId: string,
  conTermId: string,
) => {
  const resp = await apiPut<IQuoteConditionalTermSchema>(
    `/api/quotes/${quoteId}/conditionalTerms/${conTermId}/revert`,
  );
  return resp.data;
};

export function useGetQuoteDeltaview<SelectData = IQuoteOfferingDeltaView>(
  quote: IQuoteRespSchema,
  options: Partial<
    UseQueryOptions<IQuoteOfferingDeltaView, unknown, SelectData>
  > = {},
) {
  return useQuery({
    queryKey: [
      ...cpqServiceQueryKeys.quoteDeltaview(quote.id),
      quote.modifyDate,
      ...quote.quoteOfferings.map(({ modifyDate }) => modifyDate),
    ],
    queryFn: () =>
      apiGet<IQuoteOfferingDeltaView>(`/api/quotes/${quote.id}/deltaView`).then(
        (res) => {
          return res.data;
        },
      ),
    refetchOnWindowFocus: false,
    ...options,
  });
}

export function useGetQuoteShareAccess<
  SelectData = RecordShareAccessResponse[],
>(
  quoteId: string,
  options: Partial<
    UseQueryOptions<RecordShareAccessResponse[], unknown, SelectData>
  > = {},
) {
  return useQuery<RecordShareAccessResponse[], unknown, SelectData>({
    queryKey: cpqServiceQueryKeys.recordShareAccess(quoteId),
    queryFn: () =>
      apiGet<RecordShareAccessResponse[]>(
        `/api/quotes/${quoteId}/publicShare`,
      ).then((res) => res.data),
    refetchOnWindowFocus: false,
    ...options,
  });
}

export function useCreateQuoteRecordShareAccess(
  options: Partial<
    UseMutationOptions<
      RecordShareAccessResponse,
      unknown,
      QuoteShareAccessRequest
    >
  > = {},
) {
  const queryClient = useQueryClient();
  return useMutation<
    RecordShareAccessResponse,
    unknown,
    QuoteShareAccessRequest
  >({
    mutationFn: ({ quoteId, expiresAt, redirectUrl }) =>
      enqueueForQuote(quoteId, () =>
        apiPost<RecordShareAccessResponse>(
          `/api/quotes/${quoteId}/publicShare`,
          { expiresAt, redirectUrl: redirectUrl ?? undefined },
        ).then((res) => res.data),
      ),
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: cpqServiceQueryKeys.recordShareAccess(variables.quoteId),
      });
      options?.onSuccess?.(data, variables, context);
    },
  });
}

export function useRevokeQuoteRecordShare(
  options: Partial<
    UseMutationOptions<
      void,
      unknown,
      { quoteId: string; recordShareId: string }
    >
  > = {},
) {
  const queryClient = useQueryClient();
  return useMutation<void, unknown, { quoteId: string; recordShareId: string }>(
    {
      mutationFn: ({ recordShareId }) =>
        apiDelete(`/api/share/${recordShareId}/revoke`).then((res) => res.data),
      ...options,
      onSuccess: (data, variables, context) => {
        queryClient.invalidateQueries({
          queryKey: cpqServiceQueryKeys.recordShareAccess(variables.quoteId),
        });
        options?.onSuccess?.(data, variables, context);
      },
    },
  );
}

export function useQuoteRevenueMetrics(
  quoteId: string,
  options: Partial<UseQueryOptions<QuoteRevenueMetrics>> = {},
) {
  return useQuery({
    queryKey: [...cpqServiceQueryKeys.revenueMetrics(quoteId)],
    queryFn: () =>
      apiGet<QuoteRevenueMetrics>(`/api/quotes/${quoteId}/revenueMetrics`).then(
        (res) => res.data,
      ),
    refetchOnWindowFocus: false,
    ...options,
  });
}

export function useDownloadRevenueScheduleCsv(
  options: Partial<
    UseMutationOptions<
      { data: ArrayBuffer; filename: string },
      unknown,
      { quoteId: string }
    >
  > = {},
) {
  return useMutation({
    mutationFn: async ({ quoteId }) => {
      const response = await apiGet(
        `/revenue-recognition/quotes/${quoteId}/schedules`,
        {
          responseType: 'arraybuffer',
        },
      );

      return {
        data: response.data,
        filename: `revenue-schedule-${quoteId}.csv`,
      };
    },
    ...options,
  });
}

export function useDownloadRevenueJournalCsv(
  options: Partial<
    UseMutationOptions<
      { data: ArrayBuffer; filename: string },
      unknown,
      { quoteId: string }
    >
  > = {},
) {
  return useMutation({
    mutationFn: async ({ quoteId }) => {
      const response = await apiGet(
        `/revenue-recognition/quotes/${quoteId}/journalEntries`,
        {
          responseType: 'arraybuffer',
        },
      );

      return {
        data: response.data,
        filename: `revenue-journal-${quoteId}.csv`,
      };
    },
    ...options,
  });
}

export function useGetRevenueRecognitionTemplate(
  options: Partial<
    UseMutationOptions<
      { data: ArrayBuffer; filename: string },
      unknown,
      { quoteId: string }
    >
  > = {},
) {
  return useMutation({
    mutationFn: async ({ quoteId }) => {
      const response = await apiGet(
        `/revenue-recognition/quotes/${quoteId}/schedules/templates`,
        {
          responseType: 'arraybuffer',
        },
      );

      return {
        data: response.data,
        filename: `revenue-schedule-template-${quoteId}.csv`,
      };
    },
    ...options,
  });
}

export async function checkRevenueScheduleReady(
  quoteId: string,
): Promise<boolean> {
  const response = await apiGet(
    `/revenue-recognition/quotes/${quoteId}/schedules/status`,
  );
  return response.data?.status === 'GENERATED';
}

export async function checkRevenueJournalReady(
  quoteId: string,
): Promise<boolean> {
  const response = await apiGet(
    `/revenue-recognition/quotes/${quoteId}/journalEntries/status`,
  );
  return response.data?.status === 'GENERATED';
}

// BLUEPRINT QUOTES
export function useGetBlueprintQuotes(
  {
    config,
    filters,
  }: {
    config: GetListApiConfig;
    filters?: GetListApiFilter;
  },
  options: Partial<
    UseQueryOptions<ApiListResponse<BlueprintQuoteResponse>>
  > = {},
) {
  const params = composeGetQuery(config, filters);
  const response = useQuery({
    queryKey: [...cpqServiceQueryKeys.blueprintList(), params],
    queryFn: () =>
      apiGet<ApiListResponse<BlueprintQuoteResponse>>('/api/blueprintQuotes', {
        params,
      }).then((res) => res.data),
    meta: {
      setByIdCacheFromReturnedList: {
        byIdQueryKey: (id: string) => cpqServiceQueryKeys.blueprintById(id),
      },
    },
    ...options,
  });

  return response;
}

export function useGetBlueprintById(
  blueprintId: string,
  options: Partial<UseQueryOptions<BlueprintQuoteResponse>> = {},
) {
  return useQuery<BlueprintQuoteResponse>({
    queryKey: cpqServiceQueryKeys.blueprintById(blueprintId),
    queryFn: () =>
      apiGet<BlueprintQuoteResponse>(
        `/api/blueprintQuotes/${blueprintId}`,
      ).then((res) => res.data),
    enabled: !!blueprintId,
    ...options,
  });
}

export function useMarkQuoteAsBlueprint(
  options: Partial<
    UseMutationOptions<
      BlueprintQuoteResponse,
      unknown,
      {
        quoteId: string;
        payload: MarkQuoteAsBlueprintRequest;
      }
    >
  > = {},
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;
  return useMutation<
    BlueprintQuoteResponse,
    unknown,
    {
      quoteId: string;
      payload: MarkQuoteAsBlueprintRequest;
    }
  >({
    mutationFn: ({ quoteId, payload }) =>
      enqueueForQuote(quoteId, () =>
        apiPost<BlueprintQuoteResponse>(
          `/api/quotes/${quoteId}/markAsBlueprint`,
          payload,
        ).then((res) => res.data),
      ),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: cpqServiceQueryKeys.blueprintList(),
      });
      queryClient.invalidateQueries({
        queryKey: cpqServiceQueryKeys.quotesById(variables.quoteId),
      });
      onSuccess && onSuccess(data, variables, context);
    },
    ...restOptions,
  });
}

export function useUpdateBlueprintQuote(
  options: Partial<
    UseMutationOptions<
      BlueprintQuoteResponse,
      unknown,
      {
        blueprintId: string;
        payload: BlueprintQuoteUpdateRequest;
      }
    >
  > = {},
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;
  return useMutation<
    BlueprintQuoteResponse,
    unknown,
    {
      blueprintId: string;
      payload: BlueprintQuoteUpdateRequest;
    }
  >({
    mutationFn: ({ blueprintId, payload }) =>
      apiPut<BlueprintQuoteResponse>(
        `/api/blueprintQuotes/${blueprintId}`,
        payload,
      ).then((res) => res.data),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: cpqServiceQueryKeys.blueprintList(),
      });
      queryClient.setQueryData(
        cpqServiceQueryKeys.blueprintById(variables.blueprintId),
        data,
      );
      onSuccess && onSuccess(data, variables, context);
    },
    ...restOptions,
  });
}

export function useDeleteBlueprintQuote(
  options: Partial<
    UseMutationOptions<void, unknown, { blueprintId: string }>
  > = {},
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;
  return useMutation<void, unknown, { blueprintId: string }>({
    mutationFn: ({ blueprintId }) =>
      apiDelete(`/api/blueprintQuotes/${blueprintId}`).then((res) => res.data),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: cpqServiceQueryKeys.blueprintList(),
      });
      queryClient.removeQueries({
        queryKey: cpqServiceQueryKeys.blueprintById(variables.blueprintId),
      });
      onSuccess && onSuccess(data, variables, context);
    },
    ...restOptions,
  });
}

export function useUpdateQuoteBlueprint(
  options: Partial<
    UseMutationOptions<
      IQuoteRespSchema,
      unknown,
      {
        quoteId: string;
        blueprintId: string;
      }
    >
  > = {},
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;
  return useMutation<
    IQuoteRespSchema,
    unknown,
    {
      quoteId: string;
      blueprintId: string;
    }
  >({
    mutationFn: ({ quoteId, blueprintId }) =>
      enqueueForQuote(quoteId, () =>
        apiPut<IQuoteRespSchema>(`/api/quotes/${quoteId}/blueprint/`, {
          blueprintId,
        }).then((res) => sortQuoteItems(res.data)),
      ),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: cpqServiceQueryKeys.quotesById(variables.quoteId),
      });
      queryClient.invalidateQueries({
        queryKey: cpqServiceQueryKeys.quotesBasicList(),
      });
      onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
}

export function useCreateQuoteFromBlueprint(
  options: Partial<
    UseMutationOptions<
      IQuoteRespSchema,
      unknown,
      {
        blueprintId: string;
        payload: IQuoteRequestSchema;
      }
    >
  > = {},
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;
  return useMutation<
    IQuoteRespSchema,
    unknown,
    {
      blueprintId: string;
      payload: IQuoteRequestSchema;
    }
  >({
    mutationFn: ({ blueprintId, payload }) =>
      apiPost<IQuoteRespSchema>(
        `/api/quotes/${blueprintId}/createFromBlueprint`,
        payload,
      ).then((res) => sortQuoteItems(res.data)),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: cpqServiceQueryKeys.quotesBasicList(),
      });
      queryClient.setQueryData(cpqServiceQueryKeys.quotesById(data.id), data);
      onSuccess && onSuccess(data, variables, context);
    },
    ...restOptions,
  });
}

export const doGetBlueprintQuotes = async (
  config?: GetListApiConfig,
  filters?: GetListApiFilter,
): Promise<ApiListResponse<BlueprintQuoteResponse>> => {
  const params = composeGetQuery(config, filters);
  const res = await apiGet<ApiListResponse<BlueprintQuoteResponse>>(
    `/api/blueprintQuotes`,
    { params },
  );
  return res.data;
};

export function useUpdateOutputLanguageOnQuote(
  options: Partial<
    UseMutationOptions<
      void,
      unknown,
      { quoteId: string; outputLanguage: Maybe<string> }
    >
  > = {},
) {
  return useMutation<
    void,
    unknown,
    { quoteId: string; outputLanguage: Maybe<string> }
  >({
    mutationFn: ({ quoteId, outputLanguage }) =>
      enqueueForQuote(quoteId, () =>
        apiPut(`/api/quotes/${quoteId}/outputLanguage`, {
          outputLanguage: outputLanguage || null,
        }).then((res) => res.data),
      ),
    ...options,
  });
}

export function useGetQuoteOfferingGroups(
  quoteId: string,
  options: Partial<UseQueryOptions<QuoteOfferingGroups>> = {},
) {
  return useQuery({
    queryKey: [...cpqServiceQueryKeys.quoteOfferingGroups(quoteId)],
    queryFn: () =>
      apiGet<QuoteOfferingGroups>(
        `/api/quotes/${quoteId}/quoteOfferingGroups`,
      ).then((res) => res.data),
    ...options,
  });
}

export function useCreateQuoteOfferingGroup(
  quoteId: string,
  options: Partial<
    UseMutationOptions<QuoteOfferingGroups, unknown, QuoteOfferingGroupRequest>
  > = {},
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;
  return useMutation({
    mutationFn: (payload: QuoteOfferingGroupRequest) =>
      enqueueForQuote(quoteId, () =>
        apiPost<QuoteOfferingGroups>(
          `/api/quotes/${quoteId}/quoteOfferingGroups`,
          payload,
        ).then((res) => res.data),
      ),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: [...cpqServiceQueryKeys.quoteOfferingGroups(quoteId)],
      });
      onSuccess && onSuccess(data, variables, context);
    },
    ...restOptions,
  });
}
export interface UpdateQuoteOfferingGroupVariables {
  quoteOfferingGroupId: string;
  /**
   * Full group state, including the complete `quoteOfferingIds` membership
   * list — this endpoint is also how offerings are moved into and out of a
   * group.
   */
  payload: QuoteOfferingGroupRequest;
}

/** Snapshot for rolling back the optimistic membership patch on failure */
interface UpdateQuoteOfferingGroupContext {
  previousQuoteOfferingGroups?: QuoteOfferingGroups;
}

/**
 * The group id is a mutation variable rather than a hook argument so one hook
 * instance can update any group.
 *
 * Membership is patched into the groups cache optimistically: the payload is a
 * complete membership declaration, so the outcome is known before the response
 * arrives, and a move would otherwise show the offering in both its old and new
 * group until the refetch lands. The patch is rolled back on failure, and the
 * query is always refetched afterwards so the server stays authoritative.
 */
export function useUpdateQuoteOfferingGroup(
  quoteId: string,
  options: Partial<
    UseMutationOptions<
      QuoteOfferingGroups,
      unknown,
      UpdateQuoteOfferingGroupVariables,
      UpdateQuoteOfferingGroupContext
    >
  > = {},
) {
  const queryClient = useQueryClient();
  // Destructured so a caller override can't replace the cache handling below;
  // onSuccess needs no special ordering and passes through in restOptions.
  const { onMutate, onError, onSettled, ...restOptions } = options;
  const queryKey = [...cpqServiceQueryKeys.quoteOfferingGroups(quoteId)];
  return useMutation({
    mutationFn: ({
      quoteOfferingGroupId,
      payload,
    }: UpdateQuoteOfferingGroupVariables) =>
      enqueueForQuote(quoteId, () =>
        apiPut<QuoteOfferingGroups>(
          `/api/quotes/${quoteId}/quoteOfferingGroups/${quoteOfferingGroupId}`,
          payload,
        ).then((res) => res.data),
      ),
    onMutate: async (variables) => {
      // Stop an in-flight refetch from landing on top of the patch
      await queryClient.cancelQueries({ queryKey });
      const previousQuoteOfferingGroups =
        queryClient.getQueryData<QuoteOfferingGroups>(queryKey);
      if (previousQuoteOfferingGroups) {
        queryClient.setQueryData<QuoteOfferingGroups>(queryKey, {
          ...previousQuoteOfferingGroups,
          groups: applyQuoteOfferingGroupMembership(
            previousQuoteOfferingGroups.groups,
            {
              quoteOfferingGroupId: variables.quoteOfferingGroupId,
              quoteOfferingIds: variables.payload.quoteOfferingIds,
            },
          ),
        });
      }
      await onMutate?.(variables);
      return { previousQuoteOfferingGroups };
    },
    onError: (error, variables, context) => {
      if (context?.previousQuoteOfferingGroups) {
        queryClient.setQueryData(queryKey, context.previousQuoteOfferingGroups);
      }
      onError && onError(error, variables, context);
    },
    onSettled: (data, error, variables, context) => {
      queryClient.invalidateQueries({ queryKey });
      onSettled && onSettled(data, error, variables, context);
    },
    ...restOptions,
  });
}

/**
 * Deletes the group container itself. Removing an offering *from* a group is
 * an update instead — see `useUpdateQuoteOfferingGroup`.
 */
export function useDeleteQuoteOfferingGroup(
  quoteId: string,
  options: Partial<
    UseMutationOptions<QuoteOfferingGroups, unknown, string>
  > = {},
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;
  return useMutation({
    mutationFn: (quoteOfferingGroupId: string) =>
      enqueueForQuote(quoteId, () =>
        apiDelete<QuoteOfferingGroups>(
          `/api/quoteOfferingGroups/${quoteOfferingGroupId}`,
        ).then((res) => res.data),
      ),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: [...cpqServiceQueryKeys.quoteOfferingGroups(quoteId)],
      });
      onSuccess && onSuccess(data, variables, context);
    },
    ...restOptions,
  });
}
export function useGetAttachedQuoteDocuments<
  SelectData = QuoteDocumentConfig[],
>(
  quoteId: string,
  options: Partial<
    UseQueryOptions<QuoteDocumentConfig[], unknown, SelectData>
  > = {},
) {
  return useQuery({
    queryKey: [...cpqServiceQueryKeys.quoteDocumentConfigs(quoteId)],
    queryFn: () =>
      apiGet<QuoteDocumentConfig[]>(
        `/api/quotes/${quoteId}/document/configurations`,
      ).then(({ data }) => {
        return [
          ...data
            .filter(
              (doc) => doc.position === QuoteDocumentConfigPositionEnum.BEFORE,
            )
            .sort((a, b) => a.sortWithinPosition - b.sortWithinPosition),
          ...data
            .filter(
              (doc) => doc.position === QuoteDocumentConfigPositionEnum.AFTER,
            )
            .sort((a, b) => a.sortWithinPosition - b.sortWithinPosition),
        ];
      }),
    refetchOnWindowFocus: false,
    enabled: !!quoteId,
    ...options,
  });
}

export function useGetAvailableQuoteDocuments(
  quoteId: string,
  options: Partial<UseQueryOptions<QuoteDocumentConfig[]>> = {},
) {
  return useQuery({
    queryKey: [...cpqServiceQueryKeys.availableQuoteDocumentConfigs(quoteId)],
    queryFn: () =>
      apiGet<QuoteDocumentConfig[]>(
        `/api/quotes/${quoteId}/document/configurations/available`,
      ).then((res) => res.data),
    refetchOnWindowFocus: false,
    enabled: !!quoteId,
    ...options,
  });
}

export const useDeleteQuoteDocumentConfig = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      quoteId,
      documentConfigId,
    }: {
      quoteId: string;
      documentConfigId: string;
    }) =>
      enqueueForQuote(quoteId, async () => {
        const res = await apiDelete(
          `/api/quotes/${quoteId}/document/configurations/${documentConfigId}`,
        );
        return res.data;
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: cpqServiceQueryKeys.quoteDocumentConfigs(variables.quoteId),
      });
      queryClient.invalidateQueries({
        queryKey: cpqServiceQueryKeys.availableQuoteDocumentConfigs(
          variables.quoteId,
        ),
      });
    },
  });
};

export const doImportQuoteDocument = async (
  quoteId: string,
  payload: ImportDocumentRequest,
) => {
  const res = await apiPost(
    `/api/quotes/${quoteId}/document/configurations/import`,
    payload,
  );
  return res.data;
};

export const useImportDocument = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      quoteId,
      payload,
    }: {
      quoteId: string;
      payload: ImportDocumentRequest;
    }) =>
      enqueueForQuote(quoteId, () => doImportQuoteDocument(quoteId, payload)),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: cpqServiceQueryKeys.quoteDocumentConfigs(variables.quoteId),
      });
      queryClient.invalidateQueries({
        queryKey: cpqServiceQueryKeys.availableQuoteDocumentConfigs(
          variables.quoteId,
        ),
      });
    },
  });
};

export const doGetQuoteDocumentConfigDetails = async (
  quoteId: string,
  documentConfigId: string,
  attachContent = true,
  mediaType = 'application/pdf',
): Promise<QuoteDocumentConfig> => {
  const res = await apiGet(
    `/api/quotes/${quoteId}/document/configurations/${documentConfigId}`,
    {
      params: {
        attachContent,
        mediaType,
      },
    },
  );
  return res.data;
};

export const doGetQuoteParentDocumentConfigDetails = async (
  quoteId: string,
  parent: DocumentParentEnum,
  documentConfigId: string,
  attachContent = true,
): Promise<QuoteDocumentConfig> => {
  const res = await apiGet(
    `/api/quotes/${quoteId}/${parent}/document/configurations/${documentConfigId}`,
    {
      params: {
        attachContent,
      },
    },
  );
  return res.data;
};

export const useUpdateDocumentConfigurations = (quoteId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (configurations: UpdateQuoteDocument[]): Promise<void> =>
      enqueueForQuote(quoteId, async () => {
        await apiPut(
          `/api/quotes/${quoteId}/document/configurations/reorder`,
          configurations,
        );
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: cpqServiceQueryKeys.quoteDocumentConfigs(quoteId),
      });
    },
  });
};

export const doCreateQuoteDocument = async (
  quoteId: string,
  formData: FormData,
) => {
  const res = await apiUpload(
    `/api/quotes/${quoteId}/document/configurations`,
    formData,
  );
  return res.data;
};

export const useUpdateDocumentConfiguration = (quoteId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (configuration: UpdateQuoteDocument): Promise<void> =>
      enqueueForQuote(quoteId, async () => {
        await apiPut(
          `/api/quotes/${quoteId}/document/configurations/${configuration.id}`,
          configuration,
        );
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: cpqServiceQueryKeys.quoteDocumentConfigs(quoteId),
      });
    },
  });
};
